import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import Sidebar from './components/Sidebar';

export default function CreatePost() {
  const [name, setName] = useState('');
  const [author, setAuthor] = useState('');
  const [date, setDate] = useState('');
  const [slug, setSlug] = useState('');
  const [coverImage, setCoverImage] = useState('');
  const [blocks, setBlocks] = useState([]);
  const [redirect, setRedirect] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  
  const handleCoverImageChange = (ev) => {
    const file = ev.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setCoverImage(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const addBlock = (type) => {
    const newBlock = {
      id: `block-${Date.now()}`,
      type: type === 'text' ? 'text' : 'image',
      content: '',
      src: '',
      caption: '',
    };
    setBlocks([...blocks, newBlock]);
  };

  const updateBlock = (index, newContent) => {
    const updatedBlocks = [...blocks];
    updatedBlocks[index] = { ...updatedBlocks[index], ...newContent, id: updatedBlocks[index].id };
    setBlocks(updatedBlocks);
  };

  const deleteBlock = (index) => {
    setBlocks(blocks.filter((_, i) => i !== index));
  };

  const reorderBlocks = (result) => {
    if (!result.destination) return;
    const reorderedBlocks = Array.from(blocks);
    const [movedBlock] = reorderedBlocks.splice(result.source.index, 1);
    reorderedBlocks.splice(result.destination.index, 0, movedBlock);
    setBlocks(reorderedBlocks);
  };

  async function blobToBase64(blobUrl) {
    const response = await fetch(blobUrl);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  async function createNewPost(ev) {
    ev.preventDefault();
    setIsLoading(true);

    const updatedBlocks = await Promise.all(
      blocks.map(async (block) => {
        if (block.type === 'image' && block.src.startsWith('blob:')) {
          const base64Image = await blobToBase64(block.src);
          return { ...block, src: base64Image };
        }
        return block;
      })
    );

    const data = new FormData();
    data.append('name', name);
    data.append('author', author);
    data.append('date', date);
    data.append('slug', slug);
    data.append('coverImage', coverImage);
    data.append('content', JSON.stringify(updatedBlocks));

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/post`, { method: 'POST', body: data });
      if (response.ok) {
        setRedirect(true);
        alert('Blog post successfully created');
      } else {
        alert('Failed to create blog post');
      }
    } catch (error) {
      console.error('Error creating blog post:', error);
      alert('An error occurred while creating the blog post');
    } finally {
      setIsLoading(false);
    }
  }

  if (redirect) return <Navigate to={'/admin/blog'} />;

  return (
    <div style={{ 
      display: 'flex', 
      minHeight: '100vh',
      background: '#f8fafc',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    }}>
      <Sidebar />
      
      <div style={{ 
        width: '100%',
      }}>
        {/* Header */}
        <div style={{ 
          padding: '24px 40px',
          background: '#ffffff',
          borderBottom: '1px solid #e2e8f0',
          position: 'sticky',
          top: 0,
          zIndex: 10
        }}>
          <h1 style={{ 
            color: '#0f172a',
            fontSize: '28px',
            fontWeight: '700',
            margin: 0,
            letterSpacing: '-0.02em'
          }}>
            Create New Post
          </h1>
          <p style={{
            color: '#64748b',
            fontSize: '14px',
            margin: '4px 0 0 0',
            fontWeight: '400'
          }}>
            Welcome, Admin
          </p>
        </div>

        {/* Form Container */}
        <div style={{ 
          padding: '40px',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          <form onSubmit={createNewPost}>
            {/* Cover Image Section */}
            <div style={{
              background: '#ffffff',
              borderRadius: '12px',
              padding: '32px',
              marginBottom: '24px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
            }}>
              <label style={{ 
                display: 'block',
                color: '#0f172a',
                fontSize: '14px',
                fontWeight: '600',
                marginBottom: '16px'
              }}>
                Cover Image
              </label>
              <input 
                type="file" 
                onChange={handleCoverImageChange} 
                accept="image/*"
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '16px',
                  background: '#f8fafc',
                  border: '2px dashed #cbd5e1',
                  borderRadius: '8px',
                  color: '#475569',
                  cursor: 'pointer',
                  fontSize: '14px',
                  transition: 'all 0.2s ease'
                }}
              />
              {coverImage && (
                <div style={{
                  marginTop: '20px',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  border: '1px solid #e2e8f0'
                }}>
                  <img 
                    src={coverImage} 
                    alt="Cover preview" 
                    style={{ 
                      width: '100%',
                      maxWidth: '500px',
                      height: 'auto',
                      display: 'block'
                    }} 
                  />
                </div>
              )}
            </div>

            {/* Input Fields Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '24px',
              marginBottom: '24px'
            }}>
              <div style={{
                background: '#ffffff',
                padding: '24px',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
              }}>
                <label style={{
                  display: 'block',
                  color: '#0f172a',
                  fontSize: '13px',
                  fontWeight: '600',
                  marginBottom: '8px'
                }}>
                  Title
                </label>
                <input 
                  type="text"
                  placeholder="Enter post title..."
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    background: '#ffffff',
                    border: '1px solid #cbd5e1',
                    borderRadius: '6px',
                    color: '#0f172a',
                    fontSize: '14px',
                    outline: 'none',
                    transition: 'all 0.2s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#cbd5e1';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              <div style={{
                background: '#ffffff',
                padding: '24px',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
              }}>
                <label style={{
                  display: 'block',
                  color: '#0f172a',
                  fontSize: '13px',
                  fontWeight: '600',
                  marginBottom: '8px'
                }}>
                  Slug
                </label>
                <input 
                  type="text"
                  placeholder="post-url-slug"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    background: '#ffffff',
                    border: '1px solid #cbd5e1',
                    borderRadius: '6px',
                    color: '#0f172a',
                    fontSize: '14px',
                    outline: 'none',
                    transition: 'all 0.2s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#cbd5e1';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              <div style={{
                background: '#ffffff',
                padding: '24px',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
              }}>
                <label style={{
                  display: 'block',
                  color: '#0f172a',
                  fontSize: '13px',
                  fontWeight: '600',
                  marginBottom: '8px'
                }}>
                  Category
                </label>
                <select
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    background: '#ffffff',
                    border: '1px solid #cbd5e1',
                    borderRadius: '6px',
                    color: '#0f172a',
                    fontSize: '14px',
                    outline: 'none',
                    cursor: 'pointer',
                    appearance: 'none',
                    backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 12px center',
                    backgroundSize: '16px',
                    paddingRight: '40px',
                    transition: 'all 0.2s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#cbd5e1';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <option value="" style={{ background: '#ffffff', color: '#64748b' }}>Select a category...</option>
                  <option value="technology" style={{ background: '#ffffff', color: '#0f172a' }}>Technology</option>
                  <option value="design" style={{ background: '#ffffff', color: '#0f172a' }}>Design</option>
                  <option value="development" style={{ background: '#ffffff', color: '#0f172a' }}>Development</option>
                  <option value="personal" style={{ background: '#ffffff', color: '#0f172a' }}>Personal</option>
                  <option value="tutorial" style={{ background: '#ffffff', color: '#0f172a' }}>Tutorial</option>
                  <option value="review" style={{ background: '#ffffff', color: '#0f172a' }}>Review</option>
                  <option value="news" style={{ background: '#ffffff', color: '#0f172a' }}>News</option>
                  <option value="opinion" style={{ background: '#ffffff', color: '#0f172a' }}>Opinion</option>
                  <option value="lifestyle" style={{ background: '#ffffff', color: '#0f172a' }}>Lifestyle</option>
                  <option value="travel" style={{ background: '#ffffff', color: '#0f172a' }}>Travel</option>
                  <option value="other" style={{ background: '#ffffff', color: '#0f172a' }}>Other</option>
                </select>
              </div>

              <div style={{
                background: '#ffffff',
                padding: '24px',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
              }}>
                <label style={{
                  display: 'block',
                  color: '#0f172a',
                  fontSize: '13px',
                  fontWeight: '600',
                  marginBottom: '8px'
                }}>
                  Date
                </label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    background: '#ffffff',
                    border: '1px solid #cbd5e1',
                    borderRadius: '6px',
                    color: '#0f172a',
                    fontSize: '14px',
                    outline: 'none',
                    transition: 'all 0.2s ease',
                    colorScheme: 'light'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#cbd5e1';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
            </div>

            {/* Add Block Buttons */}
            <div style={{
              display: 'flex',
              gap: '16px',
              marginBottom: '32px'
            }}>
              <button
                type="button"
                onClick={() => addBlock('text')}
                style={{
                  flex: 1,
                  padding: '14px 24px',
                  background: '#3b82f6',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#2563eb';
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 8px rgba(59, 130, 246, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#3b82f6';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                }}
              >
                + Add Text Block
              </button>
              <button
                type="button"
                onClick={() => addBlock('image')}
                style={{
                  flex: 1,
                  padding: '14px 24px',
                  background: '#ffffff',
                  color: '#3b82f6',
                  border: '2px solid #3b82f6',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#eff6ff';
                  e.target.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#ffffff';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                + Add Image Block
              </button>
            </div>

            {/* Content Blocks */}
            <DragDropContext onDragEnd={reorderBlocks}>
              {blocks.length > 0 && (
                <Droppable droppableId="blocks">
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      {blocks.map((block, index) => (
                        <Draggable key={block.id} draggableId={block.id} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              style={{
                                ...provided.draggableProps.style,
                                background: snapshot.isDragging 
                                  ? '#eff6ff' 
                                  : '#ffffff',
                                borderRadius: '12px',
                                padding: '28px',
                                marginBottom: '20px',
                                border: snapshot.isDragging
                                  ? '2px solid #3b82f6'
                                  : '1px solid #e2e8f0',
                                boxShadow: snapshot.isDragging
                                  ? '0 8px 24px rgba(59, 130, 246, 0.2)'
                                  : '0 1px 3px rgba(0, 0, 0, 0.05)',
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '20px'
                              }}>
                                <span style={{
                                  color: '#64748b',
                                  fontSize: '13px',
                                  fontWeight: '600',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.05em'
                                }}>
                                  {block.type === 'text' ? '📝 Text Block' : '🖼️ Image Block'}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => deleteBlock(index)}
                                  style={{
                                    background: '#fee2e2',
                                    color: '#dc2626',
                                    border: 'none',
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.background = '#fecaca';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.background = '#fee2e2';
                                  }}
                                >
                                  Delete
                                </button>
                              </div>

                              {block.type === 'text' ? (
                                <textarea
                                  placeholder="Write your content here..."
                                  value={block.content}
                                  onChange={(e) => updateBlock(index, { content: e.target.value })}
                                  style={{
                                    width: '100%',
                                    minHeight: '180px',
                                    padding: '16px',
                                    background: '#ffffff',
                                    border: '1px solid #cbd5e1',
                                    borderRadius: '8px',
                                    color: '#0f172a',
                                    fontSize: '15px',
                                    lineHeight: '1.6',
                                    outline: 'none',
                                    resize: 'vertical',
                                    fontFamily: 'inherit',
                                    transition: 'all 0.2s ease'
                                  }}
                                  onFocus={(e) => {
                                    e.target.style.borderColor = '#3b82f6';
                                    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                                  }}
                                  onBlur={(e) => {
                                    e.target.style.borderColor = '#cbd5e1';
                                    e.target.style.boxShadow = 'none';
                                  }}
                                />
                              ) : (
                                <div>
                                  <input
                                    type="file"
                                    onChange={(e) => updateBlock(index, { src: URL.createObjectURL(e.target.files[0]) })}
                                    style={{
                                      display: 'block',
                                      width: '100%',
                                      padding: '14px',
                                      background: '#f8fafc',
                                      border: '2px dashed #cbd5e1',
                                      borderRadius: '8px',
                                      color: '#475569',
                                      cursor: 'pointer',
                                      marginBottom: '16px',
                                      fontSize: '14px'
                                    }}
                                  />
                                  {block.src && (
                                    <>
                                      <div style={{
                                        marginBottom: '16px',
                                        borderRadius: '8px',
                                        overflow: 'hidden',
                                        border: '1px solid #e2e8f0'
                                      }}>
                                        <img 
                                          src={block.src} 
                                          alt="Preview" 
                                          style={{ 
                                            width: '100%',
                                            maxWidth: '600px',
                                            height: 'auto',
                                            display: 'block'
                                          }} 
                                        />
                                      </div>
                                      <textarea
                                        placeholder="Add a caption for this image..."
                                        value={block.caption}
                                        onChange={(e) => updateBlock(index, { caption: e.target.value })}
                                        style={{
                                          width: '100%',
                                          minHeight: '80px',
                                          padding: '14px',
                                          background: '#ffffff',
                                          border: '1px solid #cbd5e1',
                                          borderRadius: '8px',
                                          color: '#0f172a',
                                          fontSize: '14px',
                                          outline: 'none',
                                          resize: 'vertical',
                                          fontFamily: 'inherit',
                                          transition: 'all 0.2s ease'
                                        }}
                                        onFocus={(e) => {
                                          e.target.style.borderColor = '#3b82f6';
                                          e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                                        }}
                                        onBlur={(e) => {
                                          e.target.style.borderColor = '#cbd5e1';
                                          e.target.style.boxShadow = 'none';
                                        }}
                                      />
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              )}
            </DragDropContext>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '16px 32px',
                background: isLoading ? '#94a3b8' : '#10b981',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                marginTop: '32px'
              }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.target.style.background = '#059669';
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 8px rgba(16, 185, 129, 0.3)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoading) {
                  e.target.style.background = '#10b981';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                }
              }}
            >
              {isLoading ? '🔄 Creating Post...' : '✨ Publish Post'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}